import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CheckCircle2,
  History,
  Loader2,
  Play,
  Sparkles,
  UserRound,
  Workflow,
} from 'lucide-react'
import Button from '../forms/Button'
import Select from '../forms/Select'
import RuleEvalResultsPanel from './RuleEvalResultsPanel'
import RulesEngineHistoryPanel from './RulesEngineHistoryPanel'
import RulesEngineValidationPanel from './RulesEngineValidationPanel'
import { useBenefitRuleMasters } from '../../hooks/useBenefitRuleMasters'
import { fetchPatientBenefitInputs } from '../../services/patientBenefitInputs'
import { getRuleSet, getRuleSetRulesCount, listRuleSets } from '../../services/rulesEngineService'
import {
  clearRuleEngineRunHistoryForPatient,
  deleteRuleEngineRunHistoryEntry,
  listRuleEngineRunHistoryForPatient,
  recordRuleEngineRun,
} from '../../services/rulesEngineHistoryService'
import type {
  BenefitInputs,
  RuleEngineRunHistoryEntry,
  RuleEngineRunResult,
  RuleSetRecord,
} from '../../types/rulesEngine'
import { runRulesEngine } from '../../utils/rulesEngineCore'
import {
  buildDynamicRuleFields,
  canonicalizeRules,
  formatDisplayValue,
  lookupBenefitValue,
  matchSelectValue,
  uniqueRuleFields,
} from '../../utils/rulesEngineFields'
import { validateRulesEngineRun } from '../../utils/validateRulesEngineRun'

type PanelTab = 'run' | 'history'

type PatientRulesEnginePanelProps = {
  patientId: string
  patientName: string
  memberId?: string
  payor?: string
  initialTab?: PanelTab
  initialBenefitInputs?: BenefitInputs
  initialRuleSetId?: string
  fromEligibility?: boolean
  patientContext?: BenefitInputs
}

function cloneBenefits(inputs: BenefitInputs): BenefitInputs {
  return { ...inputs }
}

function mergePatientValues(
  context: BenefitInputs | undefined,
  eligibility: BenefitInputs | undefined
): BenefitInputs {
  return {
    ...(context ?? {}),
    ...(eligibility ?? {}),
  }
}

export default function PatientRulesEnginePanel({
  patientId,
  patientName,
  memberId,
  payor,
  initialTab,
  initialBenefitInputs,
  initialRuleSetId,
  fromEligibility = false,
  patientContext,
}: PatientRulesEnginePanelProps) {
  const {
    fields: benefitFieldMasters,
    fieldValuesByFieldId,
    loadFieldValues,
    hasMasters,
  } = useBenefitRuleMasters(true)

  const [tab, setTab] = useState<PanelTab>(initialTab ?? 'run')
  const [ruleSets, setRuleSets] = useState<RuleSetRecord[]>([])
  const [selectedRuleSetId, setSelectedRuleSetId] = useState(initialRuleSetId ?? '')
  const [selectedRuleSet, setSelectedRuleSet] = useState<RuleSetRecord | null>(null)
  const [isLoadingRuleSet, setIsLoadingRuleSet] = useState(false)
  const [benefits, setBenefits] = useState<BenefitInputs>(() =>
    mergePatientValues(patientContext, initialBenefitInputs)
  )
  const [evalResult, setEvalResult] = useState<RuleEngineRunResult | null>(null)
  const [isRunning, setIsRunning] = useState(false)
  const [isLoadingBenefits, setIsLoadingBenefits] = useState(false)
  const [autoPopulated, setAutoPopulated] = useState(
    Boolean((fromEligibility && initialBenefitInputs) || patientContext)
  )
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [runHistory, setRunHistory] = useState<RuleEngineRunHistoryEntry[]>([])
  const [panelMessage, setPanelMessage] = useState<string | null>(null)
  const [panelMessageTone, setPanelMessageTone] = useState<'info' | 'error' | 'success'>('info')

  const notify = useCallback((message: string, tone: 'info' | 'error' | 'success' = 'info') => {
    setPanelMessage(message)
    setPanelMessageTone(tone)
  }, [])

  const loadRuleSets = useCallback(async () => {
    try {
      const rows = await listRuleSets()
      setRuleSets(rows)
      setSelectedRuleSetId((prev) => prev || initialRuleSetId || '')
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Unable to load rule sets.', 'error')
    }
  }, [initialRuleSetId, notify])

  const loadHistory = useCallback(() => {
    setRunHistory(listRuleEngineRunHistoryForPatient(patientId))
  }, [patientId])

  const loadBenefits = useCallback(async () => {
    if (fromEligibility && initialBenefitInputs) {
      setBenefits(mergePatientValues(patientContext, initialBenefitInputs))
      setAutoPopulated(true)
      return
    }

    setIsLoadingBenefits(true)
    try {
      const result = await fetchPatientBenefitInputs(patientId)
      setBenefits(mergePatientValues(patientContext, result.benefitInputs))
      setAutoPopulated(true)
    } catch (error) {
      setBenefits(mergePatientValues(patientContext, undefined))
      setAutoPopulated(Boolean(patientContext && Object.keys(patientContext).length > 0))
      notify(
        error instanceof Error
          ? `Could not load benefits: ${error.message}`
          : 'Could not load benefits for this patient.',
        'error'
      )
    } finally {
      setIsLoadingBenefits(false)
    }
  }, [fromEligibility, initialBenefitInputs, patientContext, patientId, notify])

  useEffect(() => {
    void loadRuleSets()
  }, [loadRuleSets])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  useEffect(() => {
    void loadBenefits()
  }, [loadBenefits])

  useEffect(() => {
    if (initialTab) setTab(initialTab)
  }, [initialTab])

  useEffect(() => {
    if (!selectedRuleSetId) {
      setSelectedRuleSet(null)
      setIsLoadingRuleSet(false)
      return
    }

    let cancelled = false
    setIsLoadingRuleSet(true)
    setEvalResult(null)

    const load = async () => {
      try {
        const detail = await getRuleSet(selectedRuleSetId)
        if (cancelled) return
        setSelectedRuleSet(detail)
        setRuleSets((prev) =>
          prev.map((row) =>
            row.id === detail?.id
              ? {
                  ...row,
                  ...detail,
                  rulesCount: detail.rules.length > 0 ? detail.rules.length : row.rulesCount,
                }
              : row
          )
        )
      } catch (error) {
        if (!cancelled) {
          setSelectedRuleSet(null)
          notify(error instanceof Error ? error.message : 'Unable to load rule set.', 'error')
        }
      } finally {
        if (!cancelled) setIsLoadingRuleSet(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [selectedRuleSetId, notify])

  const activeRules = useMemo(
    () =>
      canonicalizeRules(
        selectedRuleSet?.rules ?? [],
        hasMasters ? benefitFieldMasters : undefined
      ),
    [selectedRuleSet?.rules, benefitFieldMasters, hasMasters]
  )
  const requiredFieldKeys = useMemo(() => uniqueRuleFields(activeRules), [activeRules])
  const dynamicFields = useMemo(
    () =>
      buildDynamicRuleFields(
        activeRules,
        hasMasters ? benefitFieldMasters : undefined,
        fieldValuesByFieldId
      ),
    [activeRules, benefitFieldMasters, fieldValuesByFieldId, hasMasters]
  )

  useEffect(() => {
    if (dynamicFields.length === 0) return
    setBenefits((prev) => {
      let changed = false
      const next = { ...prev }
      for (const field of dynamicFields) {
        const found = lookupBenefitValue(prev, field.key)
        if (found === undefined || found === '') continue
        if (next[field.key] !== found) {
          next[field.key] = found
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [dynamicFields])

  useEffect(() => {
    const masterIds = [
      ...new Set(
        activeRules
          .map((rule) => rule.conditionFieldMasterId)
          .filter((id): id is string => Boolean(id))
      ),
    ]
    for (const id of masterIds) {
      void loadFieldValues(id)
    }
  }, [activeRules, loadFieldValues])

  const runValidation = useMemo(
    () =>
      validateRulesEngineRun({
        selectedPatientId: patientId,
        selectedRuleSet,
        autoPopulated,
        isLoadingBenefits,
        benefits,
        patientPreSelected: true,
        requiredFieldKeys,
        isLoadingRuleSet,
      }),
    [
      patientId,
      selectedRuleSet,
      autoPopulated,
      isLoadingBenefits,
      benefits,
      requiredFieldKeys,
      isLoadingRuleSet,
    ]
  )

  const handleRun = () => {
    setShowValidationErrors(true)
    if (!runValidation.valid) {
      notify('Complete all validation steps before running.', 'info')
      return
    }
    if (!selectedRuleSet) return

    setIsRunning(true)
    try {
      const result = runRulesEngine(activeRules, benefits, {
        stopOnTerminal: false,
        fieldLabels: Object.fromEntries(dynamicFields.map((field) => [field.key, field.label])),
      })
      setEvalResult(result)

      const entry = recordRuleEngineRun({
        patientId,
        patientName,
        memberId,
        payor,
        ruleSetId: selectedRuleSet.id,
        ruleSetName: selectedRuleSet.name,
        outcome: result.finalOutcome.action,
        triggerRule: result.finalOutcome.triggerRule,
        message: result.finalOutcome.message,
        benefitInputs: benefits,
        rulesEvaluated: result.results.length,
      })
      setRunHistory((prev) => [entry, ...prev.filter((e) => e.id !== entry.id)])
      notify('Rules engine completed and saved to history.', 'success')
    } catch (error) {
      notify(
        error instanceof Error ? error.message : 'Rules engine run failed. Please try again.',
        'error'
      )
    } finally {
      setIsRunning(false)
    }
  }

  const loadHistoryEntry = (entry: RuleEngineRunHistoryEntry) => {
    setTab('run')
    setSelectedRuleSetId(entry.ruleSetId)
    setBenefits(mergePatientValues(patientContext, entry.benefitInputs))
    setAutoPopulated(true)
    setShowValidationErrors(false)
    setEvalResult(null)
    notify('Loaded prior run inputs. Select Run to re-evaluate.', 'success')
  }

  const handleDeleteHistoryEntry = (id: string) => {
    deleteRuleEngineRunHistoryEntry(id)
    setRunHistory((prev) => prev.filter((e) => e.id !== id))
    notify('History entry removed.', 'success')
  }

  const handleClearHistory = () => {
    clearRuleEngineRunHistoryForPatient(patientId)
    setRunHistory([])
    notify('Run history cleared for this patient.', 'success')
  }

  const panelMessageClass =
    panelMessageTone === 'error'
      ? 'border-rose-200 bg-rose-50 text-rose-800'
      : panelMessageTone === 'success'
        ? 'border-green-200 bg-green-50 text-green-800'
        : 'border-blue-200 bg-blue-50 text-blue-800'

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-4 py-4 sm:px-6">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gray-900 text-white">
            <Workflow className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">Rules Engine</h2>
            <p className="mt-0.5 text-sm text-gray-500">
              Select a rule set to load its fields, then run them against this patient&apos;s details.
            </p>
          </div>
        </div>

        <div className="mt-4 flex gap-0 border-b border-gray-200">
          {(
            [
              { key: 'run' as const, label: 'Run Engine', icon: Play },
              { key: 'history' as const, label: `History (${runHistory.length})`, icon: History },
            ] as const
          ).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className="mb-[-1px] flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm transition-colors"
              style={{
                borderColor: tab === key ? '#111827' : 'transparent',
                color: tab === key ? '#111827' : '#6b7280',
                fontWeight: tab === key ? 700 : 500,
              }}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        {panelMessage ? (
          <div className={`mb-4 rounded-lg border px-4 py-3 text-sm ${panelMessageClass}`}>
            {panelMessage}
          </div>
        ) : null}
        {tab === 'run' ? (
          <div className={`grid gap-5 ${evalResult ? 'lg:grid-cols-2' : ''}`}>
            <div className="space-y-4">
              <RulesEngineValidationPanel
                validation={runValidation}
                showErrors={showValidationErrors}
                hidePatientCheck
              />

              <div className="rounded-2xl border border-gray-200 bg-gray-50/40 p-5">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gray-900 text-white">
                    <UserRound className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900">{patientName}</p>
                    <p className="truncate text-xs text-gray-500">
                      {payor ?? '—'}
                      {memberId ? ` · Member ${memberId}` : ''}
                    </p>
                  </div>
                  {isLoadingBenefits ? (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading patient values
                    </span>
                  ) : autoPopulated ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-600">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Patient values loaded
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Rule inputs</h3>
                    <p className="mt-1 text-xs text-gray-500">
                      Fields and patient values for the selected rule set are shown read-only.
                    </p>
                  </div>
                  {autoPopulated ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-600">
                      <Sparkles className="h-3 w-3" /> Auto-populated
                    </span>
                  ) : null}
                </div>

                <div className="mt-4">
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Rule Set
                  </label>
                  <Select
                    name="patient-rule-set"
                    value={selectedRuleSetId}
                    onChange={(e) => {
                      setSelectedRuleSetId(e.target.value)
                      setEvalResult(null)
                      setShowValidationErrors(false)
                    }}
                    options={[
                      { value: '', label: 'Select a rule set...' },
                      ...ruleSets.map((r) => ({
                        value: r.id,
                        label: `${r.name} (${getRuleSetRulesCount(r)} rules)`,
                      })),
                    ]}
                    placeholder=""
                    error={
                      showValidationErrors
                        ? runValidation.ruleSet ?? runValidation.rules ?? undefined
                        : undefined
                    }
                  />
                </div>

                {!selectedRuleSetId ? (
                  <p className="mt-4 rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
                    Select a rule set to load its configured fields.
                  </p>
                ) : isLoadingRuleSet ? (
                  <p className="mt-4 inline-flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading rule set fields…
                  </p>
                ) : dynamicFields.length === 0 ? (
                  <p className="mt-4 rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
                    This rule set has no fields to display.
                  </p>
                ) : (
                  <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {dynamicFields.map((field) => {
                      const current = lookupBenefitValue(benefits, field.key)
                      const selectValue = matchSelectValue(String(current ?? ''), field.options)
                      const displayValue = formatDisplayValue(selectValue || current)
                      return (
                        <div key={field.key}>
                          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                            {field.label}
                          </p>
                          <div
                            className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800"
                            aria-readonly="true"
                          >
                            {displayValue}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                <Button
                  type="button"
                  variant="primary"
                  className="mt-5 w-full"
                  onClick={handleRun}
                  disabled={isRunning || isLoadingBenefits || isLoadingRuleSet || !selectedRuleSetId}
                >
                  <span className="inline-flex items-center gap-2">
                    <Play className="h-4 w-4" />
                    {isRunning ? 'Running...' : 'Run Rules Engine'}
                  </span>
                </Button>
              </div>
            </div>

            {evalResult ? (
              <div className="rounded-2xl border border-gray-200 bg-white p-5">
                <p className="mb-4 text-xs text-gray-500">Patient: {patientName}</p>
                <RuleEvalResultsPanel result={evalResult} ruleSetName={selectedRuleSet?.name} />
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-bold text-gray-900">Run History</h3>
              <p className="mt-1 text-xs text-gray-500">
                Past rules engine runs for this patient — click an entry to reload inputs.
              </p>
            </div>
            <RulesEngineHistoryPanel
              entries={runHistory}
              onSelect={loadHistoryEntry}
              onDelete={handleDeleteHistoryEntry}
              onClearAll={handleClearHistory}
            />
          </div>
        )}
      </div>
    </div>
  )
}
